"use client";
import {useState, useEffect} from "react";
import ProjectCard from "@/components/ui/ProjectCard";
import {m, AnimatePresence} from "framer-motion";
import AnimatedSection from "@/components/ui/AnimatedSection";
import {type SectionProps, type Project} from "@/types/types";

const categories = ["ALL", "FRONTEND", "BACKEND", "UI", "FULL-STACK"];

interface ProjectsSectionProps extends SectionProps {
  initialProjects?: Project[];
}

export default function ProjectsSection({delay = 0, initialProjects = []}: ProjectsSectionProps) {
  const isDev = process.env.NODE_ENV === "development";
  const [projectsList, setProjectsList] = useState<Project[]>(initialProjects);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("ALL");

  useEffect(() => {
    // Keep initialProjects in sync if passed new props from the server (e.g. fast refresh)
    setProjectsList(initialProjects);
  }, [initialProjects]);

  useEffect(() => {
    if (isDev) {
      // Re-fetch locally when mutations happen
      const fetchProjects = async () => {
        try {
          const res = await fetch("/api/projects");
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data)) setProjectsList(data);
          }
        } catch (e) {
          console.error("Failed to load local projects:", e);
        }
      };

      window.addEventListener("projects-changed", fetchProjects);
      return () => window.removeEventListener("projects-changed", fetchProjects);
    }
  }, [isDev]);

  const filteredProjects = projectsList
    .filter((project) => {
      const matchesSearch =
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCategory =
        activeCategory === "ALL" ||
        project.category.toUpperCase() === activeCategory;

      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      // Prioritize featured projects
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return 0; // Maintain existing order if both are featured or neither is
    });

  // Limit to 6 projects to keep initial DOM density low
  const displayedProjects = filteredProjects.slice(0, 6);


  return (
    <AnimatedSection delay={delay} id="projects" className="py-24 border-t-2 border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <m.div
          initial={{opacity: 0, y: 20}}
          whileInView={{opacity: 1, y: 0}}
          viewport={{once: true}}
          transition={{duration: 0.4}}
          className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div>
            <h2 className="text-4xl md:text-5xl font-black uppercase text-zinc-50 tracking-tighter">
              PROJECT_LOG
            </h2>
            <div className="w-16 h-1.5 bg-yellow-400 mt-3" />
            <p className="mt-4 text-zinc-400 text-sm font-mono max-w-lg">
              Index of systems, utilities, and client builds.
            </p>
          </div>

          {/* Search Box */}
          <div className="font-mono w-full md:max-w-xs">
            <label htmlFor="search" className="block text-xs font-bold text-zinc-500 uppercase mb-2">
              Filter By Keyword
            </label>
            <input
              id="search"
              type="text"
              placeholder="SEARCH_LOG..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-zinc-950 border-2 border-zinc-800 focus:border-yellow-400 text-zinc-100 px-3 py-2 text-sm focus:outline-none transition-none rounded-none placeholder-zinc-700"
            />
          </div>
        </m.div>

        {/* Category Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-10 font-mono">
          {categories.map((cat) => {
            const isActive = activeCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                aria-pressed={isActive}
                className={`px-4 py-1.5 text-xs font-bold border transition-none cursor-pointer rounded-none ${
                  isActive
                    ? "bg-yellow-400 text-zinc-950 border-yellow-400"
                    : "bg-transparent text-zinc-400 border-zinc-800 hover:text-zinc-200 hover:border-zinc-700"
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* Dynamic Grid Layout with AnimatePresence */}
        {filteredProjects.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-800 p-12 text-center font-mono">
            <p className="text-sm text-zinc-500">NO_ENTRIES_FOUND matching the criteria.</p>
          </div>
        ) : (
          <m.div 
            layout
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 auto-rows-[420px]"
          >
            <AnimatePresence mode="popLayout">
              {displayedProjects.map((project, i) => {
                // Feature the first filtered item on desktop/tablet
                const isFeatured = i === 0 && searchTerm === "" && activeCategory === "ALL";
                return (
                  <m.div
                    layout
                    key={project.id}
                    className={isFeatured ? "sm:col-span-2 sm:row-span-1" : ""}
                    initial={{opacity: 0, scale: 0.95}}
                    animate={{opacity: 1, scale: 1}}
                    exit={{opacity: 0, scale: 0.95}}
                    transition={{duration: 0.2}}
                  >
                    <ProjectCard {...project} />
                  </m.div>
                );
              })}
            </AnimatePresence>
          </m.div>
        )}

        {filteredProjects.length > 6 && (
          <div className="mt-12 text-center">
            <a 
              href="/projects" 
              className="inline-block px-8 py-3 bg-yellow-400 text-zinc-950 font-bold font-mono text-sm tracking-wider uppercase hover:bg-yellow-300 transition-colors"
            >
              View All Projects
            </a>
          </div>
        )}
      </div>
    </AnimatedSection>
  );
}
